import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { patchWhatsAppFlowMetadata } from '../../services/api/implementation/impl';
import { WHATSAPP_FLOW_VERTICAL_OPTIONS } from '../../utils/whatsappFlowUtils';

const WhatsAppFlowMetadataModal = ({
  flowId,
  projectId,
  isOpen,
  onClose,
  initialName = '',
  initialDescription = '',
  initialVertical = ''
}) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [vertical, setVertical] = useState(initialVertical);

  useEffect(() => {
    if (!isOpen) return;
    setName(initialName || '');
    setDescription(initialDescription || '');
    setVertical(initialVertical || '');
  }, [isOpen, initialName, initialDescription, initialVertical]);

  const saveMutation = useMutation({
    mutationFn: (payload) => patchWhatsAppFlowMetadata(flowId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsappFlow', flowId] });
      if (projectId != null && projectId !== '') {
        queryClient.invalidateQueries({ queryKey: ['whatsappFlows', projectId] });
      }
      toast.success('Flow settings saved.');
      onClose();
    },
    onError: (err) => {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not save flow settings.');
    }
  });

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const trimmedVertical = vertical.trim();

  const numericProjectId = useMemo(() => {
    const parsed = Number(projectId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [projectId]);

  const isDirty =
    trimmedName !== (initialName || '').trim() ||
    trimmedDescription !== (initialDescription || '').trim() ||
    trimmedVertical !== (initialVertical || '').trim();

  const canSave =
    Boolean(trimmedName) &&
    Boolean(trimmedVertical) &&
    numericProjectId != null &&
    isDirty &&
    !saveMutation.isPending;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!trimmedName) {
      toast.error('Flow name is required.');
      return;
    }
    if (!trimmedVertical) {
      toast.error('Select a vertical.');
      return;
    }
    if (numericProjectId == null) {
      toast.error('Project is required to save flow settings.');
      return;
    }

    saveMutation.mutate({
      project_id: numericProjectId,
      name: trimmedName,
      description: trimmedDescription,
      vertical: trimmedVertical
    });
  };

  if (!isOpen) return null;

  return (
    <Modal name="Flow settings" width={38} onClose={onClose}>
      <p className="-mt-2 mb-5 text-sm text-gray-500">
        Update the flow name, description, and vertical. Step content is saved separately.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="wa-metadata-name" className="mb-1.5 block text-sm font-medium text-gray-800">
            Flow name <span className="text-red-500">*</span>
          </label>
          <input
            id="wa-metadata-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Site Visit Confirmation"
            className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <div>
          <label
            htmlFor="wa-metadata-description"
            className="mb-1.5 block text-sm font-medium text-gray-800">
            Description
          </label>
          <input
            id="wa-metadata-description"
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional description for your team"
            className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <div>
          <label htmlFor="wa-metadata-vertical" className="mb-1.5 block text-sm font-medium text-gray-800">
            Vertical <span className="text-red-500">*</span>
          </label>
          <select
            id="wa-metadata-vertical"
            value={vertical}
            onChange={(event) => setVertical(event.target.value)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
            <option value="" disabled>
              Select vertical
            </option>
            {WHATSAPP_FLOW_VERTICAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" displayType="secondary" onClick={onClose} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSave}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default WhatsAppFlowMetadataModal;
